import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useT } from 'talkr';
import s from './index.module.scss';

function SmartProgressBar() {
  const {
    currentStepIndex,
    totalSteps,
    separationStepIndexes,
    hideHeaderStepIndexes,
    hideProgressStepIndexes,
    progressTitlesBySection,
  } = useSelector((state) => state.steps);
  const { T } = useT();

  const { sections, currentSectionIndex } = useMemo(() => {
    if (
      currentStepIndex == null
      || totalSteps == null
      || !Array.isArray(separationStepIndexes)
    ) {
      return { sections: [], currentSectionIndex: -1 };
    }

    const hideHeaderSet = new Set(hideHeaderStepIndexes || []);
    const hideProgressSet = new Set(hideProgressStepIndexes || []);
    const separationSet = new Set(separationStepIndexes);

    const sectionSteps = [];
    let currentSection = [];

    for (let i = 0; i < totalSteps; i += 1) {
      const isProgressStep = !hideHeaderSet.has(i) && !hideProgressSet.has(i);

      if (isProgressStep) {
        currentSection.push(i);
      }

      if (separationSet.has(i)) {
        if (currentSection.length) {
          sectionSteps.push(currentSection);
        }
        currentSection = [];
      }
    }

    if (currentSection.length) {
      sectionSteps.push(currentSection);
    }

    const calculatedSectionIndex = sectionSteps.findIndex(
      (section) => section.includes(currentStepIndex),
    );

    const calculatedSections = sectionSteps.map((section, sectionIndex) => {
      let fill = 0;

      if (sectionIndex < calculatedSectionIndex) {
        fill = 100;
      } else if (sectionIndex === calculatedSectionIndex) {
        const passedInSection = section.filter((i) => i <= currentStepIndex).length;
        fill = (passedInSection / section.length) * 100;
      }

      return {
        id: `section-${sectionIndex}`,
        fill: Math.min(Math.max(fill, 0), 100),
      };
    });

    return {
      sections: calculatedSections,
      currentSectionIndex: calculatedSectionIndex,
    };
  }, [
    currentStepIndex,
    totalSteps,
    separationStepIndexes,
    hideHeaderStepIndexes,
    hideProgressStepIndexes,
  ]);

  const titleKey = useMemo(() => {
    if (
      !Array.isArray(progressTitlesBySection)
      || currentSectionIndex < 0
    ) {
      return null;
    }

    return progressTitlesBySection[currentSectionIndex] || null;
  }, [progressTitlesBySection, currentSectionIndex]);

  if (!sections.length) return null;

  return (
    <div className={s.wrapper}>
      <p className={s.title}>{titleKey ? T(titleKey) : null}</p>
      <div className={s.bar}>
        {sections.map((section) => (
          <div key={section.id} className={s.section}>
            <div
              className={s.sectionFill}
              style={{ width: `${section.fill}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default SmartProgressBar;
